// Field schemas per custom-section `kind`. Drives dynamic form + list rendering.
// Fields drawn from what real applications actually ask for per section type.

export const KIND_ORDER = [
  "volunteer",
  "certification",
  "language",
  "award",
  "publication",
  "project",
  "custom",
];

export const FIELD_SCHEMAS = {
  volunteer: {
    label: "Volunteer entry",
    fields: [
      { key: "organisation", label: "Organisation",  type: "text",     required: true },
      { key: "role",         label: "Role",          type: "text",     required: true },
      { key: "start_date",   label: "Start date",    type: "text",     placeholder: "e.g. Sep 2023", half: true },
      { key: "end_date",     label: "End date",      type: "text",     placeholder: "e.g. Jun 2024 or leave blank", half: true },
      { key: "currently",    label: "Currently volunteering here", type: "checkbox" },
      { key: "location",     label: "Location",      type: "text",     placeholder: "optional" },
      { key: "description",  label: "Responsibilities / description", type: "textarea" },
    ],
    titleFrom: (f) => {
      if (f.role && f.organisation) return `${f.role} — ${f.organisation}`;
      return f.organisation || f.role || "Untitled";
    },
    metaLine: (f) => {
      const parts = [];
      if (f.start_date || f.end_date) {
        const end = f.currently ? "present" : (f.end_date || "");
        parts.push(`${f.start_date || ""}${end ? ` – ${end}` : ""}`.trim());
      }
      if (f.location) parts.push(f.location);
      return parts.filter(Boolean).join(" · ");
    },
  },

  certification: {
    label: "Certification",
    fields: [
      { key: "name",           label: "Certification name",  type: "text",     required: true },
      { key: "issuer",         label: "Issuing organisation", type: "text" },
      { key: "issue_date",     label: "Issue date",  type: "text", placeholder: "e.g. Mar 2024", half: true },
      { key: "expiry_date",    label: "Expiry date", type: "text", placeholder: "e.g. Mar 2027", half: true },
      { key: "credential_id",  label: "Credential ID", type: "text" },
      { key: "credential_url", label: "Credential URL", type: "url", placeholder: "https://…" },
    ],
    titleFrom: (f) => f.name || "Untitled certification",
    metaLine: (f) => {
      const bits = [];
      if (f.issuer) bits.push(f.issuer);
      if (f.issue_date) bits.push(`issued ${f.issue_date}`);
      if (f.expiry_date) bits.push(`expires ${f.expiry_date}`);
      if (f.credential_id) bits.push(`ID: ${f.credential_id}`);
      return bits.join(" · ");
    },
  },

  language: {
    label: "Language",
    fields: [
      { key: "language",     label: "Language",    type: "text",   required: true },
      { key: "proficiency",  label: "Proficiency", type: "select",
        options: ["Basic", "Conversational", "Fluent", "Native / Bilingual"] },
    ],
    titleFrom: (f) => f.language || "Untitled",
    metaLine: (f) => f.proficiency || "",
  },

  award: {
    label: "Award",
    fields: [
      { key: "name",         label: "Award name",           type: "text",     required: true },
      { key: "issuer",       label: "Awarding organisation", type: "text" },
      { key: "date",         label: "Date awarded",         type: "text",     placeholder: "e.g. 2024" },
      { key: "description",  label: "Description",          type: "textarea" },
    ],
    titleFrom: (f) => f.name || "Untitled award",
    metaLine: (f) => [f.issuer, f.date].filter(Boolean).join(" · "),
  },

  publication: {
    label: "Publication",
    fields: [
      { key: "title",     label: "Title",             type: "text",     required: true },
      { key: "publisher", label: "Publisher / journal", type: "text" },
      { key: "date",      label: "Publication date",  type: "text",     placeholder: "e.g. Nov 2023" },
      { key: "authors",   label: "Authors",           type: "text",     placeholder: "comma-separated" },
      { key: "url",       label: "URL / DOI",         type: "url",      placeholder: "https://…" },
    ],
    titleFrom: (f) => f.title || "Untitled publication",
    metaLine: (f) => [f.publisher, f.date, f.authors].filter(Boolean).join(" · "),
  },

  project: {
    label: "Project",
    fields: [
      { key: "name",         label: "Project name",  type: "text",     required: true },
      { key: "role",         label: "Your role",     type: "text" },
      { key: "start_date",   label: "Start date",    type: "text",     half: true },
      { key: "end_date",     label: "End date",      type: "text",     placeholder: "or 'ongoing'", half: true },
      { key: "url",          label: "URL",           type: "url",      placeholder: "https://…" },
      { key: "tech",         label: "Tech / stack",  type: "text",     placeholder: "e.g. React, Node, Postgres" },
      { key: "description",  label: "Description",   type: "textarea" },
    ],
    titleFrom: (f) => f.name || "Untitled project",
    metaLine: (f) => {
      const dates = [f.start_date, f.end_date].filter(Boolean).join(" – ");
      return [f.role, dates, f.tech].filter(Boolean).join(" · ");
    },
  },

  // Fully user-defined free-form section. Uses legacy top-level fields for storage
  // so old items keep working unchanged.
  custom: {
    label: "Item",
    legacy: true,  // reads/writes to top-level fields (title, subtitle, date, url, body) not `fields` dict
    fields: [
      { key: "title",    label: "Title",       type: "text",     required: true },
      { key: "subtitle", label: "Subtitle",    type: "text",     placeholder: "e.g. issuer, org, subheading" },
      { key: "date",     label: "Date",        type: "text",     placeholder: "e.g. 2024 or 'ongoing'" },
      { key: "url",      label: "URL",         type: "url",      placeholder: "https://…" },
      { key: "body",     label: "Description", type: "textarea" },
    ],
    titleFrom: (f) => f.title || "Untitled",
    metaLine: (f) => [f.subtitle, f.date].filter(Boolean).join(" · "),
  },
};

export function schemaFor(kind) {
  return FIELD_SCHEMAS[kind] || FIELD_SCHEMAS.custom;
}

// Given an item + section, return the working values object the form should edit.
// For `custom` sections, use the legacy top-level fields.
// For typed kinds, use `item.fields` (falling back to legacy fields for migrated items).
export function extractFormValues(item, kind) {
  const sc = schemaFor(kind);
  if (sc.legacy) {
    return {
      title:    item?.title    || "",
      subtitle: item?.subtitle || "",
      date:     item?.date     || "",
      url:      item?.url      || "",
      body:     item?.body     || "",
    };
  }
  const source = item?.fields && Object.keys(item.fields).length ? item.fields : {};
  const out = {};
  for (const f of sc.fields) out[f.key] = source[f.key] ?? "";
  return out;
}

// Convert form values back into the API payload for POST/PUT /custom/items.
export function toApiPayload(values, kind, sectionId) {
  const sc = schemaFor(kind);
  if (sc.legacy) {
    return {
      section_id: sectionId,
      title:    values.title    || "",
      subtitle: values.subtitle || "",
      date:     values.date     || "",
      url:      values.url      || "",
      body:     values.body     || "",
      fields:   {},
    };
  }
  // Typed kind: title is derived, everything else lives in `fields`.
  const title = sc.titleFrom(values) || "Untitled";
  return {
    section_id: sectionId,
    title,
    subtitle: "",
    date: "",
    url: values.url || values.credential_url || "",  // convenience for extension autofill
    body: "",
    fields: { ...values },
  };
}

// Concept key hint the extension uses to match this section to form fields.
// Falls back to name-regex logic (extension-side) if kind is missing.
export function conceptKeyFor(kind, sectionId) {
  if (kind === "custom") return `custom:${sectionId}`;
  return `custom_kind:${kind}`;
}
